import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EquipmentFailureMode, FailurePattern } from '../../../viewmodels/equipment/equipment-failure-mode';
import { FormControlBase, SeparatorControl } from '../../dynamic-forms/form-models/form-control-base';
import { MinMaxNumberControl } from '../../dynamic-forms/form-models/minmaxnumber-control';
import { NumberControl } from '../../dynamic-forms/form-models/number-control';
import { PropertyPath } from '../../dynamic-forms/form-models/property-path';
import { ModelFunctionsService } from '../model-functions.service';

@Component({
  selector: 'hb-failure-mode-parameters-dialog',
  templateUrl: './failure-mode-parameters-dialog.component.html',
  styleUrls: ['./failure-mode-parameters-dialog.component.scss'],
})
export class FailureModeParametersDialogComponent implements OnInit, OnDestroy {
  public title: string;
  public selectedFailureMode: EquipmentFailureMode;
  private unsubscribe: Subject<boolean> = new Subject();
  public onClose: Subject<EquipmentFailureMode> = new Subject<EquipmentFailureMode>();

  private readonly shapeCtrl = new MinMaxNumberControl({
    key: 'shape',
    label: 'Shape (γ)',
    required: true,
    options: { min: '0' },
    propertyName: PropertyPath.get((r: EquipmentFailureMode) => r.shape),
  });

  private readonly locationCtrl = new NumberControl({
    key: 'location',
    label: 'Location (μ)',
    required: true,
    propertyName: PropertyPath.get((r: EquipmentFailureMode) => r.location),
  });

  private readonly scaleCtrl = new MinMaxNumberControl({
    key: 'scale',
    label: 'Scale (α)',
    required: true,
    options: { min: '0' },
    propertyName: PropertyPath.get((r: EquipmentFailureMode) => r.scale),
  });

  private readonly nCtrl = new MinMaxNumberControl({
    key: 'n',
    label: 'Number of Defects (n)',
    required: true,
    options: { min: '0' },
    propertyName: PropertyPath.get((r: EquipmentFailureMode) => r.nfactor),
    integerOnly: true,
  });

  private readonly kCtrl = new MinMaxNumberControl({
    key: 'k',
    label: 'Number of Failures (k)',
    required: true,
    options: { min: '0' },
    propertyName: PropertyPath.get((r: EquipmentFailureMode) => r.kfactor),
    integerOnly: true,
  });

  private readonly separatorOther = new SeparatorControl({
    key: 'OtherParameters',
    label: 'Other',
    required: false,
    propertyName: null,
  });

  private readonly weibullOther = new SeparatorControl({
    key: 'WeibullParameters',
    label: 'Weibull',
    required: false,
    propertyName: null,
  });

  public controls: FormControlBase<any>[] = [
    this.weibullOther,
    this.shapeCtrl,
    this.locationCtrl,
    this.scaleCtrl,
    this.separatorOther,
    this.nCtrl,
    this.kCtrl,
  ];

  @Input() editable: boolean = true;

  constructor(private modelFunctionsService: ModelFunctionsService, public bsModalRef: BsModalRef) {}

  ngOnInit() {
    this.title = 'Parameters';
    this.modelFunctionsService.selectedFailureMode.pipe(takeUntil(this.unsubscribe)).subscribe(failureMode => {
      if (failureMode) {
        this.selectedFailureMode = failureMode;
        this.shapeCtrl.disabled= !this.editable;
        this.locationCtrl.disabled= !this.editable;
        this.scaleCtrl.disabled= !this.editable;
        this.nCtrl.disabled = !this.editable;
        this.kCtrl.disabled =  !this.editable;
      }
    });

    this.setWeibullParametersRequired();
  }

  private setWeibullParametersRequired(): void {
    if (this.selectedFailureMode) {
      const weibullParametersRequired = this.selectedFailureMode.failurePattern !== FailurePattern.Random;
      this.shapeCtrl.required = weibullParametersRequired;
      this.locationCtrl.required = weibullParametersRequired;
      this.scaleCtrl.required = weibullParametersRequired;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onSaved() {
    this.onClose.next(this.selectedFailureMode);
    this.bsModalRef.hide();
  }
}
